import React, { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import EDMRichEditor from '../EDM/EDMRichEditor';
import { updateAsset, CampaignAsset } from '../../services/api';
import './BlogPostEditorModal.css';

interface BlogPostEditorModalProps {
    asset: CampaignAsset;
    campaignId: string;
    onClose: () => void;
    onSaved: (updated: CampaignAsset) => void;
}

const BlogPostEditorModal: React.FC<BlogPostEditorModalProps> = ({
    asset,
    campaignId,
    onClose,
    onSaved,
}) => {
    const [content, setContent] = useState(asset.content ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const updated = await updateAsset(campaignId, asset.asset_id, { content });
            onSaved(updated);
            onClose();
        } catch (err: any) {
            setError(err?.response?.data?.detail ?? 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="blog-editor-overlay" onClick={onClose}>
            <div className="blog-editor-modal" onClick={e => e.stopPropagation()}>
                <div className="blog-editor-header">
                    <span className="blog-editor-title">
                        {asset.title ?? 'Blog Post'}
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                        {error && (
                            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>{error}</span>
                        )}
                        <GlassButton variant="secondary" size="sm" onClick={onClose} disabled={saving}>
                            Cancel
                        </GlassButton>
                        <GlassButton variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                            {saving ? <><Loader2 size={14} className="spin" /> Saving…</> : <><Save size={14} /> Save</>}
                        </GlassButton>
                    </div>
                </div>
                <div className="blog-editor-body">
                    <EDMRichEditor
                        content={content}
                        onChange={setContent}
                        placeholder="Write your blog post here…"
                    />
                </div>
            </div>
        </div>
    );
};

export default BlogPostEditorModal;
